import { getQueueSessions } from '@api/queries/getQueueSessions';
import IAppLogger from '@app/lib/IAppLogger';
import appManager from '@app/appManager';
import { serviceState } from './state';

type SyncStatus = 'pending' | 'syncing' | 'synced' | 'error';

type QueueSyncItem = {
  queueId: string;
  status: SyncStatus;
  retryCount: number;
  lastError?: string;
};

type SyncConfig = {
  maxConcurrent: number;
  staggerDelayMs: number;
  maxRetries: number;
  batchCooldownMs: number; // Cooldown between batches during sync
  syncCooldownMs: number; // Cooldown after completing full sync cycle
};

const DEFAULT_CONFIG: SyncConfig = {
  maxConcurrent: 5,
  staggerDelayMs: 200,
  maxRetries: 2,
  batchCooldownMs: 1000, // 1 second cooldown between batches during sync
  syncCooldownMs: 5 * 60 * 1000, // 5 minutes cooldown after completing sync cycle
};

/**
 * QueueSessionSyncManager
 *
 * Manages periodic synchronization of queue sessions from PCS API.
 * Lives within ServiceModule and is mounted/unmounted by the workspace
 * lifecycle (openWorkspace/closeWorkspace) so it only runs when the
 * workspace is properly connected.
 *
 * Key behaviors:
 * - checkAndEnqueue() tracks out-of-sync queues (called from setQueueStatus)
 * - mount() starts periodic sync loop (every 5 minutes by default)
 * - Each sync cycle: syncs all out-of-sync queues in batches
 * - unmount() stops periodic sync and aborts in-flight requests
 * - Throttling: max 5 concurrent, 200ms stagger within batch, 1s between batches, 5min between cycles
 */
export class QueueSessionSyncManager {
  private readonly syncQueue: Map<string, QueueSyncItem> = new Map();
  private processing = false;
  private readonly config: SyncConfig;
  private abortController: AbortController | null = null;
  private enabled = false;
  private syncIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly logger: IAppLogger;

  constructor(config: Partial<SyncConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = appManager.logger('QueueSessionSyncManager');
  }

  /**
   * Called by setQueueStatus when mismatch detected.
   * Tracks out-of-sync queues but does NOT trigger immediate processing.
   * Processing happens periodically via the sync interval.
   */
  checkAndEnqueue(
    queueId: string,
    activeSessionCount: number,
    currentSessionsLength: number,
  ): void {
    // Queue is in sync - remove from tracking
    if (activeSessionCount === currentSessionsLength) {
      const wasTracked = this.syncQueue.has(queueId);
      this.syncQueue.delete(queueId);

      if (wasTracked) {
        this.logger.debug(
          '[QueueSessionSyncManager] Queue now in sync, removed from tracking',
          {
            queueId,
            activeSessionCount,
            currentSessionsLength,
          },
        );
      }
      return;
    }

    // Already syncing this queue - don't interfere
    const existing = this.syncQueue.get(queueId);
    if (existing?.status === 'syncing') {
      this.logger.debug(
        '[QueueSessionSyncManager] Queue already syncing, skipping enqueue',
        {
          queueId,
        },
      );
      return;
    }

    // Add/update in sync queue (always, regardless of enabled state)
    // Periodic sync will process this later
    const isNew = !existing;
    this.syncQueue.set(queueId, {
      queueId,
      status: 'pending',
      retryCount: existing?.retryCount ?? 0,
    });

    this.logger.debug('[QueueSessionSyncManager] Queue mismatch detected', {
      queueId,
      activeSessionCount,
      currentSessionsLength,
      action: isNew ? 'added_to_queue' : 'updated_in_queue',
      totalQueueSize: this.syncQueue.size,
    });
  }

  /**
   * Called when the workspace connects. Starts periodic sync loop.
   */
  mount(): void {
    // Prevent duplicate intervals if already mounted
    if (this.syncIntervalId) {
      this.logger.debug(
        '[QueueSessionSyncManager] Already mounted, ignoring duplicate mount() call',
      );
      return;
    }

    this.enabled = true;

    this.logger.debug(
      '[QueueSessionSyncManager] Mounted - starting periodic sync',
      {
        syncIntervalSeconds: this.config.syncCooldownMs / 1000,
      },
    );

    // Initial check: enqueue any queues already out of sync at mount time
    for (const queue of serviceState.queues) {
      this.checkAndEnqueue(
        queue.id,
        queue.status.activeSessionCount ?? 0,
        queue.sessions.length,
      );
    }

    // Sync immediately on mount if there are out-of-sync queues
    if (this.syncQueue.size > 0) {
      this.logger.debug('[QueueSessionSyncManager] Starting initial sync', {
        outOfSyncQueueCount: this.syncQueue.size,
      });
      void this.runSyncCycle();
    } else {
      this.logger.debug(
        '[QueueSessionSyncManager] No out-of-sync queues on mount, waiting for mismatches',
      );
    }

    // Start periodic sync interval
    this.syncIntervalId = setInterval(() => {
      this.logger.debug(
        '[QueueSessionSyncManager] Periodic sync interval triggered',
        {
          enabled: this.enabled,
          processing: this.processing,
          queueSize: this.syncQueue.size,
        },
      );

      if (this.enabled && !this.processing) {
        void this.runSyncCycle();
      }
    }, this.config.syncCooldownMs);
  }

  /**
   * Called when the workspace disconnects. Stops periodic sync, aborts in-flight syncs.
   * Note: Does NOT clear the queue - mismatches continue to be tracked.
   */
  unmount(): void {
    this.enabled = false;
    this.abortController?.abort();

    // Stop periodic sync interval
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = null;
    }
    // Don't set processing = false here - let processSyncQueue's finally block handle it

    this.logger.debug(
      '[QueueSessionSyncManager] Unmounted - stopped periodic sync',
      {
        remainingQueuedItems: this.syncQueue.size,
      },
    );
    // Note: syncQueue is NOT cleared - we keep tracking mismatches
  }

  /**
   * Runs a single sync cycle: checks for out-of-sync queues and syncs them.
   * Called periodically by the sync interval.
   */
  private async runSyncCycle(): Promise<void> {
    if (!this.enabled) {
      this.logger.debug(
        '[QueueSessionSyncManager] Sync cycle skipped - manager not enabled',
      );
      return;
    }

    if (this.processing) {
      this.logger.debug(
        '[QueueSessionSyncManager] Sync cycle skipped - already processing',
      );
      return;
    }

    const outOfSyncCount = this.syncQueue.size;
    if (outOfSyncCount === 0) {
      this.logger.debug(
        '[QueueSessionSyncManager] Sync cycle skipped - no out-of-sync queues',
      );
      return;
    }

    this.logger.debug('[QueueSessionSyncManager] Starting sync cycle', {
      outOfSyncQueueCount: outOfSyncCount,
    });

    await this.processSyncQueue();

    this.logger.debug('[QueueSessionSyncManager] Sync cycle completed', {
      remainingOutOfSync: this.syncQueue.size,
    });
  }

  private async processSyncQueue(): Promise<void> {
    if (this.processing) return;
    this.processing = true;
    this.abortController = new AbortController();

    try {
      while (this.syncQueue.size > 0 && !this.abortController.signal.aborted) {
        const pending = Array.from(this.syncQueue.values()).filter(
          (item) => item.status === 'pending',
        );

        if (pending.length === 0) {
          // No pending items, but there might be items currently syncing
          // Wait a bit for in-flight requests to complete and potentially fail
          await new Promise((r) => setTimeout(r, 100));

          // Check again for newly pending items (from failed syncs)
          const stillPending = Array.from(this.syncQueue.values()).filter(
            (item) => item.status === 'pending',
          );

          if (stillPending.length === 0) break;
          continue; // Process the newly pending items
        }

        const batch = pending.slice(0, this.config.maxConcurrent);
        await Promise.allSettled(
          batch.map((item, i) =>
            this.syncQueueItem(item, i * this.config.staggerDelayMs),
          ),
        );

        // Cooldown period between batches to avoid overwhelming the API
        if (
          this.syncQueue.size > 0 &&
          !this.abortController?.signal.aborted &&
          this.enabled
        ) {
          await new Promise((r) => setTimeout(r, this.config.batchCooldownMs));
        }
      }
    } finally {
      this.processing = false;
      this.abortController = null;
    }
  }

  private async syncQueueItem(
    item: QueueSyncItem,
    delayMs: number,
  ): Promise<void> {
    if (delayMs > 0) await new Promise((r) => setTimeout(r, delayMs));

    const syncItem = this.syncQueue.get(item.queueId);
    if (!syncItem || this.abortController?.signal.aborted || !this.enabled) {
      return;
    }

    syncItem.status = 'syncing';

    try {
      const { sessions } = await getQueueSessions(
        item.queueId,
        this.abortController?.signal,
      );

      const queue = serviceState.queueById[item.queueId];
      if (queue) {
        queue.sessions = sessions;
        this.logger.debug(
          '[QueueSessionSyncManager] Successfully synced queue',
          {
            queueId: item.queueId,
            sessionCount: sessions.length,
          },
        );
      } else {
        this.logger.debug(
          '[QueueSessionSyncManager] Queue not found for session update',
          { queueId: item.queueId },
        );
      }

      // Always remove from sync queue after successful API call
      this.syncQueue.delete(item.queueId);
    } catch (error) {
      syncItem.status = 'error';
      syncItem.retryCount += 1;
      syncItem.lastError =
        error instanceof Error ? error.message : 'Unknown error';

      this.logger.error('[QueueSessionSyncManager] Failed to sync queue', {
        queueId: item.queueId,
        retryCount: syncItem.retryCount,
        error: error instanceof Error ? error.message : String(error),
      });

      if (syncItem.retryCount >= this.config.maxRetries) {
        this.logger.error(
          '[QueueSessionSyncManager] Max retries reached, removing from queue',
          {
            queueId: item.queueId,
            maxRetries: this.config.maxRetries,
          },
        );
        this.syncQueue.delete(item.queueId);
      } else {
        syncItem.status = 'pending';
      }
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current sync queue size (for debugging/monitoring)
   */
  getQueueSize(): number {
    return this.syncQueue.size;
  }

  /**
   * Get sync item status (for debugging/monitoring)
   */
  getSyncItem(queueId: string): QueueSyncItem | undefined {
    return this.syncQueue.get(queueId);
  }
}

export const queueSessionSyncManager = new QueueSessionSyncManager();
